import React, { useEffect, useState } from 'react'
import { BUTTON_THEME, CircularLoader, Button } from '@st-operations/uikit/src/v2/index.js'
import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1/index.js'

import { logErrorAsync } from '_lib/utility'
import { useRetractSidebar, useModal } from '_hooks'

import Header from '_components/header/index.jsx'
import MediaMeasurementCampaignsStatusBadge from '_components/media-measurement-campaign-status-badge'
import MediaMeasurementCampaignSubmittedModal from '_components/media-measurement-campaign-submitted-modal'

import MediaMeasurementTabView from '_components/media-measurement-tab-view'
import AudienceSettings from '_components/online-media-measurements/tab-audience-settings'
import CandidateUrlsList from '_components/online-media-measurements/tab-candidate-urls-list'
import DeliveryDetails from '_components/online-media-measurements/tab-delivery-details'
import InsightsReports from '_components/online-media-measurements/tab-insights-reports'
import DeliveryModal from '_components/online-media-measurements/delivery-modal'
import ConfirmationModal from '_components/online-media-measurements/confirmation-modal'
import InvalidDetails from '_components/online-media-measurements/invalid-details'
import {
  OMM_STATUS_OPTIONS,
  OMM_REPORTS_STATUS_OPTIONS,
} from '_components/online-media-measurements/constants'

import { getMediaMeasurementCampaigns, updateMediaMeasurementCampaigns } from '_services/campaigns'
import { newLog } from '_services/logs'

const ENTITY = 'media-measurement-campaigns'

const addSelection = (urls, selectedUrls) => {
  if (selectedUrls.length) {
    // audience was already delivered
    return urls.map((url) => ({
      ...url,
      selected: selectedUrls.includes(url.canonicalUrl),
    }))
  } else if (urls.at(0)?.hasOwnProperty('selected')) {
    // has data regarding any url (meaning that this audience was saved once)
    return urls
  } else {
    // select default based on score
    return urls.map((url) => ({
      ...url,
      selected: url.score === 1,
    }))
  }
}

const MediaMeasurementCampaign = (props) => {
  const { id } = props.match.params

  const [inRequest, setInRequest] = useState(false)

  const submittedModal = useModal(false)
  const deliveryModal = useModal(false)
  const confirmationModal = useModal(false)
  const reportsConfirmationModal = useModal(false)

  const [data, setData] = useState(null)
  const transformData = (data) => ({
    ...data,
    candidateUrls: addSelection(data.candidateUrls, data.selectedUrls),
  })
  const updateData = (name) => (value) => setData((prev) => ({ ...prev, [name]: value }))
  const updateReports = (name) => (value) =>
    setData((prev) => ({
      ...prev,
      reports: {
        ...(prev?.reports ?? {}),
        [name]: {
          ...(prev?.reports?.[name] ?? {}),
          selected: value,
        },
      },
    }))

  const selectedUsers = data?.candidateUrls
    ?.filter((url) => url.selected)
    ?.reduce((acc, cur) => acc + cur.users, 0)

  const updateSelected = (type, list) => {
    const canonicalList = list.map(({ canonicalUrl }) => canonicalUrl)
    const bool = type === 'add' ? true : false
    const newSelected = data.candidateUrls.map((url) =>
      canonicalList.includes(url.canonicalUrl) ? { ...url, selected: bool } : url
    )
    updateData('candidateUrls')(newSelected)
  }

  const TABS = {
    SETTINGS: {
      title: 'Audience Settings',
      content: (
        <AudienceSettings
          data={data}
          updateState={updateData}
          updateReports={updateReports}
          status={data?.status}
        />
      ),
    },
    URLS: {
      title: 'Candidate URLs List',
      content: (
        <CandidateUrlsList
          data={data}
          updateSelected={updateSelected}
          selectedUsers={selectedUsers}
        />
      ),
    },
    DELIVERY:
      window._isShareThisUser || data?.deliveries?.[0]?.sizeCheck === 'Pass' // ⬇️
        ? {
            title: 'Delivery Details',
            content: <DeliveryDetails data={data} />,
          }
        : null,
    REPORTS: {
      title: 'Insight Reports Beta',
      content: <InsightsReports data={data} />,
    },
    INVALID: {
      title: 'Invalid Details',
      content: <InvalidDetails data={data} />,
    },
  }

  const TABS_BY_STATUS = {
    [OMM_STATUS_OPTIONS.DRAFT]: [TABS.SETTINGS, TABS.REPORTS],
    [OMM_STATUS_OPTIONS.SUBMITTED_FOR_PROCESSING]: [TABS.SETTINGS, TABS.REPORTS],
    [OMM_STATUS_OPTIONS.PROCESSING]: [TABS.SETTINGS, TABS.REPORTS],
    [OMM_STATUS_OPTIONS.PROCESSED]: [TABS.SETTINGS, TABS.URLS, TABS.REPORTS],
    [OMM_STATUS_OPTIONS.SUBMITTED_FOR_DELIVERY]: [TABS.SETTINGS, TABS.URLS, TABS.REPORTS],
    [OMM_STATUS_OPTIONS.DELIVERING]: [TABS.SETTINGS, TABS.URLS, TABS.REPORTS],
    [OMM_STATUS_OPTIONS.DELIVERED]: [TABS.SETTINGS, TABS.URLS, TABS.DELIVERY, TABS.REPORTS],
    [OMM_STATUS_OPTIONS.INVALID]: [TABS.SETTINGS, TABS.INVALID],
    [OMM_STATUS_OPTIONS.SIZE_LOW]: [TABS.SETTINGS, TABS.URLS, TABS.INVALID],
  }

  useRetractSidebar()

  useEffect(() => {
    const load = async (id) => {
      try {
        const data = await getMediaMeasurementCampaigns(id)
        setData(transformData(data))
      } catch (error) {
        logErrorAsync(error)
        toast.error(error.message)
      }
    }

    load(id)
  }, [id])

  const handleSaveAs = async (status, customRoute = false, extraData = {}, log = false) => {
    if (inRequest) return
    setInRequest(true)

    const newData = { ...data, ...extraData, status }

    try {
      await updateMediaMeasurementCampaigns(id, newData).then((item) => {
        if (!log) return
        newLog({
          entity: ENTITY,
          data: {
            action: status,
            audienceName: item.name,
            audienceId: item._id,
          },
        })
      })

      if (!customRoute) {
        toast.success('Update successful!')
        handleExit()
      }
      return true
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    } finally {
      setInRequest(false)
    }
  }

  const handleSave = async (logAction = '') => {
    if (inRequest) return
    setInRequest(true)

    try {
      await updateMediaMeasurementCampaigns(id, data).then((item) => {
        newLog({
          entity: ENTITY,
          data: {
            action: logAction,
            audienceName: item.name,
            audienceId: item._id,
          },
        })
      })
      toast.success('Update successful!')
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    } finally {
      setInRequest(false)
    }
  }

  const handleSubmitForProcessing = async () => {
    const isSaved = await handleSaveAs(OMM_STATUS_OPTIONS.SUBMITTED_FOR_PROCESSING, true, {}, true)
    if (isSaved) submittedModal.handleOpenModal()
  }

  const handleDeliver = async () => {
    if (inRequest) return
    setInRequest(true)

    const selectedUrls = data.candidateUrls
      .filter((url) => url.selected)
      .map(({ canonicalUrl }) => canonicalUrl)

    const payload = {
      ...data,
      status: OMM_STATUS_OPTIONS.SUBMITTED_FOR_DELIVERY,
      selectedUrls,
    }

    if (selectedUrls.length === 0) {
      toast.error('Select URLs to deliver')
      setInRequest(false)
      return
    }

    try {
      await updateMediaMeasurementCampaigns(id, payload).then((item) => {
        newLog({
          entity: ENTITY,
          data: {
            action: item.status,
            audienceName: item.name,
            audienceId: item._id,
          },
        })
      })
      deliveryModal.handleOpenModal()
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    } finally {
      setInRequest(false)
      confirmationModal.handleCloseModal()
    }
  }

  const handleGenerateReports = async () => {
    if (inRequest) return
    setInRequest(true)

    const selectedUrls = data.candidateUrls
      .filter((url) => url.selected)
      .map(({ canonicalUrl }) => canonicalUrl)

    const payload = {
      ...data,
      reportsStatus: OMM_REPORTS_STATUS_OPTIONS.REQUESTED,
      selectedUrls,
    }

    if (selectedUrls.length === 0) {
      toast.error('Select URLs to request reports')
      setInRequest(false)
      return
    }

    try {
      await updateMediaMeasurementCampaigns(id, payload).then((item) => {
        toast.success('Reports Requested')
        setData(transformData(item))
        newLog({
          entity: ENTITY,
          data: {
            action: 'requested reports',
            audienceName: item.name,
            audienceId: item._id,
          },
        })
      })
    } catch (error) {
      logErrorAsync(error)
      toast.error(error.message)
    } finally {
      setInRequest(false)
    }
  }

  const handleExit = () => {
    props.history.push(`/online-media-measurements`)
  }

  const handleCloseSubmittedModal = async (emailNotification) => {
    submittedModal.handleCloseModal()
    await handleSaveAs(OMM_STATUS_OPTIONS.SUBMITTED_FOR_PROCESSING, true, {
      emailNotification,
    })
    handleExit()
  }

  if (!data) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <CircularLoader />
      </div>
    )
  }

  return (
    <>
      <Header
        title={
          <div className="flex flex-row gap-4 items-center">
            <span>Online Media Measurement</span>
            <MediaMeasurementCampaignsStatusBadge status={data.status} />
          </div>
        }
      >
        <Button theme={BUTTON_THEME.GHOST} onClick={handleExit}>
          Exit
        </Button>
        <If condition={data.status === OMM_STATUS_OPTIONS.DRAFT}>
          <Button theme={BUTTON_THEME.SECONDARY} onClick={() => handleSave('save draft')}>
            Save as Draft
          </Button>
          <Button theme={BUTTON_THEME.PRIMARY} onClick={handleSubmitForProcessing}>
            Submit For Processing
          </Button>
        </If>
        <If condition={data.status === OMM_STATUS_OPTIONS.PROCESSED}>
          <Button
            theme={BUTTON_THEME.SECONDARY}
            onClick={() => handleSave('save url selection')}
            disabled={data?.selectedUrls?.length}
          >
            Save Selection
          </Button>
        </If>
        <If
          condition={[
            OMM_STATUS_OPTIONS.PROCESSED,
            OMM_STATUS_OPTIONS.SUBMITTED_FOR_DELIVERY,
            OMM_STATUS_OPTIONS.DELIVERING,
            OMM_STATUS_OPTIONS.DELIVERED,
          ].includes(data.status)}
        >
          <Button
            theme={BUTTON_THEME.SECONDARY}
            onClick={reportsConfirmationModal.handleOpenModal}
            disabled={
              (data?.reportsStatus && data?.reportsStatus !== OMM_REPORTS_STATUS_OPTIONS.DRAFT) ||
              selectedUsers < 25
            }
          >
            Generate Reports
          </Button>
        </If>
        <If condition={data.status === OMM_STATUS_OPTIONS.PROCESSED}>
          <Button
            theme={BUTTON_THEME.PRIMARY}
            onClick={confirmationModal.handleOpenModal}
            disabled={selectedUsers < 25}
          >
            Deliver
          </Button>
        </If>
      </Header>

      <MediaMeasurementTabView tabs={TABS_BY_STATUS[data.status].filter(Boolean)} />

      <Dialog open={deliveryModal.isOpen} onClose={deliveryModal.handleCloseModal}>
        <DeliveryModal onClose={deliveryModal.handleCloseModal} handleExit={handleExit} />
      </Dialog>

      <Dialog open={confirmationModal.isOpen} onClose={confirmationModal.handleCloseModal}>
        <ConfirmationModal
          onConfirm={handleDeliver}
          onCancel={confirmationModal.handleCloseModal}
          data={{
            title: 'Delivering Audience',
            description: [
              'Are you sure you’d like to deliver this audience? There will be a cost associated once the audience is delivered.',
              'Effective 6/1/25, cost per audience delivery is now $650.',
            ],
            confirmButtonText: inRequest ? 'Delivering...' : 'Deliver Audience',
            cancelButtonText: 'Continue Editing',
          }}
        />
      </Dialog>

      <Dialog
        open={reportsConfirmationModal.isOpen}
        onClose={reportsConfirmationModal.handleCloseModal}
      >
        <ConfirmationModal
          onConfirm={handleGenerateReports}
          onCancel={reportsConfirmationModal.handleCloseModal}
          data={{
            title: 'Charge notification',
            description: [
              'Generating OMM Insight Reports will automatically incur a charge.',
              'Please confirm if you’d like to proceed.',
            ],
            confirmButtonText: inRequest ? 'Generating...' : 'Generate Reports',
            cancelButtonText: 'Cancel',
          }}
        />
      </Dialog>

      <MediaMeasurementCampaignSubmittedModal
        open={submittedModal.isOpen}
        onConfirm={handleCloseSubmittedModal}
      />
    </>
  )
}

export default MediaMeasurementCampaign
